import React, { useEffect, useState } from 'react';

import { NavLinkButton } from '@shared/components/bootstrap';

import { Preview as AddressPreview } from '@admin/components/address/preview';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';

type AddressPreviewProps = React.ComponentProps<typeof AddressPreview>;

import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import {
  attachPrimaryCustomers,
  fetchPrimaryCustomersForPallets,
  fetchTransferPool,
  fetchTransferPoolPallets,
} from './api';
import { TransferPoolPalletsList } from './pallets_list';
import { TransferPool, TransferPoolPallet } from './types';

export const TransferPoolShow: React.FC = () => {
  useTitle('Transfer Pool');
  const { id } = useParams<{ id: string }>();
  const [transferPool, setTransferPool] = useState<TransferPool>();
  const [pallets, setPallets] = useState<TransferPoolPallet[]>();
  const [loadingPool, setLoadingPool] = useState(true);
  const [loadingPallets, setLoadingPallets] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setLoadingPool(true);
    setError(undefined);

    fetchTransferPool(id)
      .then((data) => {
        if (!active) return;
        setTransferPool(data);
        setLoadingPool(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Transfer pool not found.');
        setLoadingPool(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setLoadingPallets(true);
    setPallets(undefined);

    fetchTransferPoolPallets(id)
      .then(async (data) => {
        const customers = await fetchPrimaryCustomersForPallets(data);
        if (!active) return;
        setPallets(attachPrimaryCustomers(data, customers));
        setLoadingPallets(false);
      })
      .catch(() => {
        if (!active) return;
        setPallets([]);
        setLoadingPallets(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  if (loadingPool) {
    return <Spinner />;
  }

  if (error || !transferPool) {
    return <div className="alert alert-danger">{error ?? 'Transfer pool not found.'}</div>;
  }

  const title = `${transferPool.origin.name} to ${transferPool.destination.name}`;

  return (
    <>
      <h1 className="page-header text-overflow">
        <ol className="breadcrumb">
          <li>
            <NavLink to="/transferpools">Transfer Pool</NavLink>
          </li>
          <li className="active">{title}</li>
        </ol>
      </h1>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>Origin:</strong> <span>{transferPool.origin.name}</span>{' '}
            <span>
              (<AddressPreview address={transferPool.origin.address as AddressPreviewProps['address']} mode="inline" />)
            </span>
          </p>
          <p>
            <strong>Destination:</strong> <span>{transferPool.destination.name}</span>{' '}
            <span>
              (
              <AddressPreview
                address={transferPool.destination.address as AddressPreviewProps['address']}
                mode="inline"
              />
              )
            </span>
          </p>
        </Panel.Body>
        <Panel.Footer align="right">
          <NavLinkButton to={`/transferpools/${encodeURIComponent(id)}/edit`} kind="primary">
            Edit
          </NavLinkButton>
        </Panel.Footer>
      </Panel>

      <TransferPoolPalletsList pallets={pallets} loading={loadingPallets} />
    </>
  );
};
